import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  AccountModel,
  AllocationTypeModel,
  AssetModel,
  AssetTypeModel,
  ObjectModel,
  ObjectRelation,
  ObjectRelationSetting,
  PagedResult,
  Period,
  SupplierModel,
  VatCodeModel,
} from './models/master-data.models';
import { Configuration } from '../../libs/shared/configuration';
import { FilterOperation } from '@rillion/api/models/common';

export interface ObjectFilters {
  company?: string;
  accountCodingDate?: string;
  objectTypeNo?: number;
  nameFilter?: string;
}

@Injectable({ providedIn: 'root' })
export class MasterDataApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly configuration: Configuration,
  ) {}

  getAccounts(role: string, accountIds: number[] = []) {
    const keys: string[] = [];
    const operations: string[] = [];
    const values: string[] = [];

    if (accountIds.length) {
      keys.push(`accountId`);
      operations.push(FilterOperation.Or);
      values.push(accountIds.join('|'));
    }
    return this.http.get<PagedResult<AccountModel>>(
      `${this.configuration.basePath}/api/v1.0/account/role/${role}?pagesize=10000&sort=favourite&direction=desc&keys=${keys}&operations=${operations}&values=${values}`,
    );
  }

  getVatCodes(role: string) {
    return this.http.get<PagedResult<VatCodeModel>>(`${this.configuration.basePath}/api/v1.0/vatcode/role/${role}?pagesize=1000000`);
  }

  getObjects(role: string, filters?: ObjectFilters) {
    const keys: string[] = [];
    const operations: string[] = [];
    const values: string[] = [];

    if (filters?.company) {
      keys.push(`company`);
      operations.push(FilterOperation.Or);
      values.push(`${filters?.company}|null`);
    }

    if (filters?.accountCodingDate) {
      keys.push(`validFrom;validTo`.toLowerCase());
      operations.push(`${FilterOperation.LessThanEqualNullable};${FilterOperation.GreaterThanEqualNullable}`);
      values.push(`${filters?.accountCodingDate};${filters?.accountCodingDate}`);
    }

    if (filters?.objectTypeNo) {
      keys.push(`objectTypeNo`);
      operations.push(FilterOperation.Equal);
      values.push(`${filters?.objectTypeNo}`);
    }

    if (filters?.nameFilter) {
      keys.push(`name`);
      operations.push(FilterOperation.Contains);
      values.push(`${filters?.nameFilter}`);
    }

    const queryParams = keys.length ? `&keys=${keys.join(';')}&operations=${operations.join(';')}&values=${values.join(';')}` : '';

    return this.http.get<PagedResult<ObjectModel>>(`${this.configuration.basePath}/api/v1.0/object/role/${role}?pagesize=1000000&sort=favourite&direction=desc${queryParams}`);
  }

  getObjectRelations() {
    return this.http.get<PagedResult<ObjectRelation>>(`${this.configuration.basePath}/api/v1.0/objectRelation?pagesize=1000000`);
  }

  getObjectRelationSettings() {
    return this.http.get<PagedResult<ObjectRelationSetting>>(`${this.configuration.basePath}/api/v1.0/objectRelationSetting?pagesize=1000000`);
  }

  getAllocationTypes() {
    return this.http.get<PagedResult<AllocationTypeModel>>(`${this.configuration.basePath}/api/v1.0/allocationType`);
  }

  getAsset(role: string) {
    return this.http.get<PagedResult<AssetModel>>(`${this.configuration.basePath}/api/v1.0/asset/role/${role}?pagesize=1000000`);
  }

  getAssetTypes(role: string) {
    return this.http.get<PagedResult<AssetTypeModel>>(`${this.configuration.basePath}/api/v1.0/assetType/role/${role}?pagesize=1000000`);
  }

  getSupplier(role: string, supplierId: number) {
    return this.http.get<PagedResult<SupplierModel>>(`${this.configuration.basePath}/api/v1.0/supplier/role/${role}?keys=supplierId&operations=eq&values=${supplierId}`);
  }

  getValidPeriod(role: string, company: string, startDate: string, endDate: string) {
    return this.http.get<PagedResult<Period>>(
      `${this.configuration.basePath}/api/v1.0/period/role/${role}?pagesize=1&pageindex=0&keys=company;startDate;endDate&operations=or;lte;gte&values=${company};${startDate};${endDate}`,
    );
  }
}
